import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { toast } from 'react-toastify';
import {
  useRuleKindsQuery, useCreateColumnMutation, useUpdateColumnMutation,
} from '../../api/taskColumnsApi';
import { apiErrorMessage } from '../../utils/apiError';

/**
 * Create/edit a board column.
 *
 * This component deliberately knows NOTHING about any particular rule kind. It renders the kind
 * picker from `/task-columns/kinds` and then renders one input per declared parameter, choosing the
 * control from the parameter's `type`. Adding a rule kind on the server therefore makes it
 * configurable here with no frontend change — which is what the clinic's "…and so on" requires.
 */
export default function ColumnBuilderModal({ column, onClose, onSaved }) {
  const editing = !!column?.id;
  const { data: kinds = [], isLoading: loadingKinds } = useRuleKindsQuery();
  const [createColumn, { isLoading: creating }] = useCreateColumnMutation();
  const [updateColumn, { isLoading: updating }] = useUpdateColumnMutation();

  const [name, setName] = useState(column?.name ?? '');
  const [ruleKind, setRuleKind] = useState(column?.ruleKind ?? '');
  const [values, setValues] = useState(() => ({ ...(column?.parameters ?? {}) }));
  const saving = creating || updating;

  const selected = useMemo(() => kinds.find((k) => k.kind === ruleKind), [kinds, ruleKind]);

  // Pick a sensible kind once the catalogue arrives, and seed the defaults it declares.
  useEffect(() => {
    if (ruleKind || kinds.length === 0) return;
    setRuleKind(kinds[0].kind);
  }, [kinds, ruleKind]);

  useEffect(() => {
    if (!selected) return;
    setValues((current) => {
      const next = { ...current };
      selected.parameters.forEach((p) => {
        if (next[p.name] === undefined || next[p.name] === null) next[p.name] = p.default ?? '';
      });
      return next;
    });
  }, [selected]);

  const setParam = (paramName, value) => setValues((v) => ({ ...v, [paramName]: value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error('Give the column a name.'); return; }
    if (!ruleKind) { toast.error('Choose what this column should look for.'); return; }

    // Only send parameters the chosen kind declares, so switching kind can't smuggle stale keys.
    const parameters = {};
    (selected?.parameters ?? []).forEach((p) => {
      const v = values[p.name];
      parameters[p.name] = v === '' || v === undefined ? null : String(v);
    });

    try {
      const body = { name: name.trim(), ruleKind, parameters };
      if (editing) await updateColumn({ id: column.id, ...body }).unwrap();
      else await createColumn(body).unwrap();
      toast.success(editing ? 'Column updated' : 'Column added');
      onSaved?.();
      onClose();
    } catch (err) {
      // Parameter validation errors come back written for the person editing the column.
      toast.error(apiErrorMessage(err));
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={submit}>
          <div className="modal-header">
            <h2>{editing ? 'Edit column' : 'New column'}</h2>
            <button type="button" className="modal-close" onClick={onClose}><X size={18} /></button>
          </div>

          <div className="modal-body">
            <div className="form-group">
              <label className="form-label" htmlFor="col-name">Column name *</label>
              <input
                id="col-name"
                className="form-control"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Chase 3-day no-shows"
                autoFocus
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="col-kind">What should this column look for? *</label>
              <select
                id="col-kind"
                className="form-control"
                value={ruleKind}
                onChange={(e) => { setRuleKind(e.target.value); setValues({}); }}
                disabled={loadingKinds}
              >
                {kinds.map((k) => <option key={k.kind} value={k.kind}>{k.label}</option>)}
              </select>
              {selected?.description && (
                <div className="td-muted" style={{ fontSize: '0.78rem', marginTop: 5 }}>
                  {selected.description}
                </div>
              )}
            </div>

            {(selected?.parameters ?? []).length > 0 && (
              <div className="card" style={{ marginTop: 4 }}>
                <div className="card-body" style={{ display: 'grid', gap: 10 }}>
                  {selected.parameters.map((p) => (
                    <ParamField
                      key={p.name}
                      def={p}
                      value={values[p.name] ?? ''}
                      onChange={(v) => setParam(p.name, v)}
                    />
                  ))}
                </div>
              </div>
            )}

            <div className="td-muted" style={{ fontSize: '0.78rem', marginTop: 10 }}>
              Everything is measured against the board's date, so changing that date re-runs this
              column for that day.
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : editing ? 'Save changes' : 'Add column'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/** One parameter input, chosen by the schema's declared type. */
function ParamField({ def, value, onChange }) {
  const id = `param-${def.name}`;

  if (def.type === 'Bool') {
    return (
      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: '0.85rem' }}>
        <input
          id={id}
          type="checkbox"
          checked={String(value) === 'true'}
          onChange={(e) => onChange(e.target.checked ? 'true' : 'false')}
        />
        <span>
          {def.label}
          {def.helpText && <div className="td-muted" style={{ fontSize: '0.75rem' }}>{def.helpText}</div>}
        </span>
      </label>
    );
  }

  return (
    <div>
      <label className="form-label" htmlFor={id} style={{ fontSize: '0.8rem' }}>
        {def.label}{def.required ? ' *' : ''}
      </label>

      {def.type === 'Enum' ? (
        <select id={id} className="form-control" value={value} onChange={(e) => onChange(e.target.value)}>
          {(def.options ?? []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      ) : (
        <input
          id={id}
          className="form-control"
          type={def.type === 'Int' || def.type === 'Decimal' ? 'number' : def.type === 'Date' ? 'date' : 'text'}
          step={def.type === 'Decimal' ? '0.01' : undefined}
          min={def.min ?? undefined}
          max={def.max ?? undefined}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={def.required ? '' : 'Leave blank to ignore'}
        />
      )}

      {def.helpText && (
        <div className="td-muted" style={{ fontSize: '0.75rem', marginTop: 3 }}>{def.helpText}</div>
      )}
    </div>
  );
}
